import React from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Add,
  Download,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { toggleOnboardingTaskAsync, toggleHrChecklistAsync } from "../../store/slices/dataSlice";
import { showSnackbar } from "../../store/slices/uiSlice";
import StatCard from "../../components/shared/StatCard";

const Onboarding: React.FC = () => {
  const dispatch = useAppDispatch();
  const joinees = useAppSelector((s) => s.data.onboarding);
  const config = useAppSelector((s) => s.data.onboardingConfig);

  const totalJoinees = joinees.length;
  // A simple heuristic for docs pending: anyone with progress < 30% might still be pending docs, or we count specific tasks if available.
  const docsPending = joinees.filter((j) => j.progress < 50).length;
  const itSetupDone = joinees.filter((j) => 
    j.tasks.some((t) => t.task.toLowerCase().includes('it setup') && t.done) || j.progress >= 80
  ).length;
  const avgProgress = totalJoinees > 0 
    ? Math.round(joinees.reduce((sum, j) => sum + j.progress, 0) / totalJoinees) 
    : 0;

  const dynamicStats = [
    {
      label: "New Joinees",
      value: totalJoinees.toString(),
      icon: "GroupAdd",
      delta: "Joining soon",
      deltaType: "up" as const,
      color: "#85D1DB",
      iconBg: "rgba(133,209,219,0.1)",
    },
    {
      label: "Docs Pending",
      value: docsPending.toString(),
      icon: "FolderOpen",
      delta: "Awaiting upload",
      deltaType: docsPending > 0 ? "down" as const : "neutral" as const,
      color: "#dc2626",
      iconBg: "#fee2e2",
    },
    {
      label: "IT Setup Done",
      value: itSetupDone.toString(),
      icon: "Computer",
      delta: `Of ${totalJoinees} joinees`,
      deltaType: "neutral" as const,
      color: "#16a34a",
      iconBg: "#dcfce7",
    },
    {
      label: "Avg Progress",
      value: `${avgProgress}%`,
      icon: "Timeline",
      delta: "Overall",
      deltaType: "neutral" as const,
      color: "#d97706",
      iconBg: "#fef3c7",
    },
  ];

  const hrChecklist = config?.hrChecklist || [];
  const schedule = config?.schedule || [];

  return (
    <Box>
      <SEO title="Onboarding" noindex />
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {dynamicStats.map((s) => (
          <Grid item xs={6} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {joinees.map((j) => (
              <Grid item xs={12} sm={6} key={j.id}>
                <Card sx={{ p: 0, height: "100%" }}>
                  <CardContent sx={{ p: "18px !important" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          background: "linear-gradient(135deg,#85D1DB,#B6F2D1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {j.name[0]}
                        {j.name.split(" ")[1]?.[0] || ""}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {j.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                          {j.role} · {j.dept}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: "#94a3b8", mt: 0.3 }}
                        >
                          📅 Joining {j.joinDate}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        Progress
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#85D1DB",
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        {j.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={j.progress}
                      sx={{
                        height: 6,
                        borderRadius: 99,
                        backgroundColor: "#e2e8f0",
                        mb: 1.5,
                        "& .MuiLinearProgress-bar": {
                          background: "#85D1DB",
                          borderRadius: 99,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.8,
                      }}
                    >
                      {j.tasks.map((t, i) => (
                        <Box
                          key={i}
                          sx={{
                            cursor: "pointer",
                            p: "6px 8px",
                            borderRadius: 1.5,
                            transition: "background 0.15s",
                            "&:hover": { background: "#f8fafc" },
                          }}
                          onClick={() =>
                            dispatch(
                              toggleOnboardingTaskAsync({
                                id: j.id,
                                taskIdx: i,
                              }),
                            )
                          }
                        >
                          {t.done ? (
                            <CheckCircle
                              sx={{
                                fontSize: 20,
                                color: "#16a34a",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <RadioButtonUnchecked
                              sx={{
                                fontSize: 20,
                                color: "#cbd5e1",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: "#0f172a",
                              textDecoration: t.done ? "line-through" : "none",
                              opacity: t.done ? 0.5 : 1,
                            }}
                          >
                            {t.task}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ p: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                HR Onboarding Checklist
              </Typography>
            </Box>
            <CardContent sx={{ p: "12px 16px !important" }}>
              {hrChecklist.map((t, i) => (
                <Box
                  key={i}
                  sx={{
                    cursor: "pointer",
                    p: "8px",
                    borderRadius: 1.5,
                    borderBottom: "1px solid #f1f5f9",
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { background: "#f8fafc" },
                    transition: "background 0.15s",
                  }}
                  onClick={() => dispatch(toggleHrChecklistAsync(i))}
                >
                  {t.done ? (
                    <CheckCircle
                      sx={{ fontSize: 20, color: "#16a34a", flexShrink: 0 }}
                    />
                  ) : (
                    <RadioButtonUnchecked
                      sx={{ fontSize: 20, color: "#cbd5e1", flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 13,
                      textDecoration: t.done ? "line-through" : "none",
                      opacity: t.done ? 0.5 : 1,
                    }}
                  >
                    {t.task}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
          <Card>
            <Box sx={{ p: "12px 16px", borderBottom: "1px solid #e5e7eb" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                Orientation — May 3
              </Typography>
            </Box>
            <CardContent
              sx={{
                p: "12px 16px !important",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {schedule.map((s, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      fontFamily: "'JetBrains Mono',monospace",
                      color: "#85D1DB",
                      minWidth: 44,
                      pt: 0.2,
                    }}
                  >
                    {s.time}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>
                      {s.title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}
                    >
                      {s.sub}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Onboarding;
